import { apiClient } from '../axios-instance'
import type { AxiosResponse } from 'axios'
import type { Merchant } from '../../../features/merchants/types/merchant.types'

export interface PaginatedResponse<T> {
  data: T[]
  meta: {
    current_page: number
    from: number
    last_page: number
    per_page: number
    to: number
    total: number
  }
}

export interface MerchantFilters {
  status?: string
  kyc_status?: string
  source?: string
  search?: string
  page?: number
  per_page?: number
  sort_by?: string
  sort_order?: 'asc' | 'desc'
}

export interface CreateMerchantRequest {
  user_id: number
  business_name: string
  business_type: 'individual' | 'company' | 'partnership'
  tax_id?: string
  registration_number?: string
  full_name?: string
  email?: string
  mobile?: string
  address_line1?: string
  city?: string
  state?: string
  pincode?: string
  latitude?: number
  longitude?: number
}

export interface UpdateMerchantRequest {
  business_name?: string
  business_type?: 'individual' | 'company' | 'partnership'
  tax_id?: string
  registration_number?: string
}

export const merchantsService = {
  /**
   * Get paginated list of merchants
   */
  getMerchants: async (filters?: MerchantFilters): Promise<PaginatedResponse<Merchant>> => {
    const response: AxiosResponse<{ success: boolean; data: Merchant[]; meta: any }> = await apiClient.get(
      '/admin/merchants',
      { params: filters }
    )
    return {
      data: response.data.data,
      meta: response.data.meta
    }
  },

  /**
   * Get merchant by ID
   */
  getMerchantById: async (merchantId: string): Promise<Merchant> => {
    const response: AxiosResponse<{ success: boolean; data: Merchant }> = await apiClient.get(`/admin/merchants/${merchantId}`)
    return response.data.data
  },

  /**
   * Create new merchant
   */
  createMerchant: async (data: CreateMerchantRequest): Promise<Merchant> => {
    const response: AxiosResponse<{ success: boolean; data: Merchant }> = await apiClient.post('/admin/merchants', data)
    return response.data.data
  },

  /**
   * Update merchant
   */
  updateMerchant: async (merchantId: string, data: UpdateMerchantRequest): Promise<Merchant> => {
    const response: AxiosResponse<{ success: boolean; data: Merchant }> = await apiClient.put(`/admin/merchants/${merchantId}`, data)
    return response.data.data
  },

  /**
   * Delete merchant
   */
  deleteMerchant: async (merchantId: string): Promise<void> => {
    await apiClient.delete(`/admin/merchants/${merchantId}`)
  },

  /**
   * Verify KYC
   */
  verifyKyc: async (merchantId: string): Promise<Merchant> => {
    const response: AxiosResponse<{ success: boolean; data: Merchant }> = await apiClient.put(`/admin/merchants/${merchantId}/kyc/approve`)
    return response.data.data
  },

  /**
   * Bulk Verify KYC
   */
  bulkVerifyKyc: async (merchantIds: React.Key[]): Promise<Merchant[]> => {
    const response: AxiosResponse<{ success: boolean; data: Merchant[] }> = await apiClient.put('/admin/merchants/kyc/bulk-approve', { merchant_ids: merchantIds })
    return response.data.data
  },

  /**
   * Reject KYC
   */
  rejectKyc: async (merchantId: string, rejection_reason: string): Promise<Merchant> => {
    const response: AxiosResponse<{ success: boolean; data: Merchant }> = await apiClient.put(`/admin/merchants/${merchantId}/kyc/reject`, { rejection_reason })
    return response.data.data
  },

  /**
   * Suspend merchant
   */
  suspendMerchant: async (merchantId: string, reason?: string): Promise<Merchant> => {
    const response: AxiosResponse<{ success: boolean; data: Merchant }> = await apiClient.post(`/admin/merchants/${merchantId}/suspend`, { reason })
    return response.data.data
  },

  /**
   * Unsuspend merchant
   */
  unsuspendMerchant: async (merchantId: string): Promise<Merchant> => {
    const response: AxiosResponse<{ success: boolean; data: Merchant }> = await apiClient.post(`/admin/merchants/${merchantId}/unsuspend`)
    return response.data.data
  },

  /**
   * Unblock merchant
   */
  unblockMerchant: async (merchantId: string): Promise<Merchant> => {
    const response: AxiosResponse<{ success: boolean; data: Merchant }> = await apiClient.post(`/admin/merchants/${merchantId}/unblock`)
    return response.data.data
  },

   /**
   * Block/Reject merchant
   */
  rejectMerchant: async (merchantId: string, reason: string): Promise<Merchant> => {
      const response: AxiosResponse<{ success: boolean; data: Merchant }> = await apiClient.post(`/admin/merchants/${merchantId}/reject`, { reason })
      return response.data.data
  },

  /**
   * Verify PAN document (Admin only)
   */
  verifyPan: async (merchantId: string): Promise<Merchant> => {
    const response: AxiosResponse<{ success: boolean; data: Merchant }> = await apiClient.put(`/admin/merchants/${merchantId}/kyc/verify-pan`)
    return response.data.data
  },

  /**
   * Verify GST document (Admin only)
   */
  verifyGst: async (merchantId: string): Promise<Merchant> => {
    const response: AxiosResponse<{ success: boolean; data: Merchant }> = await apiClient.put(`/admin/merchants/${merchantId}/kyc/verify-gst`)
    return response.data.data
  },

  /**
   * Verify bank details (Admin only)
   */
  verifyBank: async (merchantId: string): Promise<void> => {
    await apiClient.put(`/admin/merchants/${merchantId}/bank-details/verify`, { verified: true })
  },

  /**
   * Check availability of a specific field (email, mobile, pan_number, gst_number)
   */
  checkAvailability: async (type: string, value: string): Promise<boolean> => {
    const response: AxiosResponse<{ success: boolean; available: boolean }> = await apiClient.get(
      '/admin/merchants/check-availability',
      { params: { type, value } }
    )
    return response.data.available
  },

  /**
   * Fetch a secure document download URL
   */
  downloadDocument: async (urlOrUuid: string): Promise<string> => {
    if (urlOrUuid.startsWith('http://') || urlOrUuid.startsWith('https://')) {
      return urlOrUuid;
    }
    
    // Clean up UUID if it accidentally has path parts
    let uuid = urlOrUuid;
    const parts = uuid.split('/');
    uuid = parts[parts.length - 1];

    const response = await apiClient.get(`/documents/${uuid}/download-url`);
    return response.data.data.download_url;
  }
}
